import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { FormControl } from '@mui/material';
import { ImageUploadServices } from '../components/ImgUpload/imageUploadSrevices';
import Swal from 'sweetalert2';

const TextEditor = ({ fieldName, rules, imageFolder, hideToolBar, disableEditor }) => {
  const { control, formState: { errors }, setValue, getValues } = useFormContext();
  const quillRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Clean HTML content by removing empty paragraphs
  const cleanHtmlContent = useCallback((content) => {
    if (!content) return '';
    return content.replace(/<p><br><\/p>/g, '').trim();
  }, []);

  // Initialize with clean content
  useEffect(() => {
    const currentValue = getValues(fieldName);
    if (currentValue && !isInitialized) {
      const cleanedContent = cleanHtmlContent(currentValue);
      setValue(fieldName, cleanedContent);
      setIsInitialized(true);
    }
  }, [fieldName, getValues, setValue, cleanHtmlContent, isInitialized]);

  // Memoized function to upload an image
  const uploadImage = useCallback(async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', imageFolder);

      Swal.fire({
        title: 'Uploading Image',
        text: 'Please wait...',
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await ImageUploadServices.uploadImage(formData);
      Swal.close();

      return response?.data?.imageBaseUrl + response?.data?.imageDirectory + 'md/' + response?.data?.imageName || null;
    } catch (error) {
      console.error('Error uploading file:', error);
      return null;
    }
  }, [imageFolder]);

  // Image handler for Quill editor
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (file) {
        try {
          const url = await uploadImage(file);
          if (url && quillRef.current) {
            const editor = quillRef.current.getEditor();
            const range = editor.getSelection();
            editor.insertEmbed(range?.index || 0, 'image', url);
          }
        } catch (error) {
          console.error('Image upload failed:', error);
        }
      }
    };
  }, [uploadImage]);

  // Editor modules with image handling
  const modules = React.useMemo(() => ({
    toolbar: !hideToolBar ? {
      container: [
        [{ font: [] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }, { size: ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline'],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image', 'video'],
      ],
      handlers: {
        image: imageHandler,
      },
    } : false,
  }), [hideToolBar, imageHandler]);

  return (
    <FormControl
      fullWidth
      sx={{
        '& .ql-container': {
          height: hideToolBar ? '100%' : '90%',
          fontSize: '0.875rem',
          backgroundColor:  '#fff',
        },
        '& .ql-toolbar.ql-snow': {
          display: hideToolBar ? 'none' : 'block',
          borderColor: errors[fieldName] ? '#d32f2f' : '#cccccc',
          borderWidth: '1px',
          padding: '4px 8px',
          '& .ql-formats': {
            marginRight: '8px',
            '&:last-child': {
              marginRight: 0,
            },
          },
          '& button': {
            height: '24px',
            width: '24px',
            padding: '2px',
          },
          '& .ql-picker': {
            height: '24px',
            '& .ql-picker-label': {
              padding: '0 4px',
              fontSize: '0.75rem',
            },
            '& .ql-picker-options': {
              fontSize: '0.75rem',
            },
          },
        },
        '& .ql-container.ql-snow': {
          borderColor: errors[fieldName] ? '#d32f2f' : '#cccccc',
          borderWidth: '1px',
          fontSize: '0.875rem',
          backgroundColor: '#fff',
          cursor: disableEditor ? 'not-allowed' : 'text',
          '& .ql-editor': {
            padding: '12px',
            '& p': {
              fontSize: '0.875rem',
            },
            '& h1': { fontSize: '1.5rem' },
            '& h2': { fontSize: '1.25rem' },
            '& h3': { fontSize: '1.125rem' },
          },
        },
        '& .ql-toolbar + .ql-container': {
          borderTop: hideToolBar ? '1px solid #cccccc' : 'none',
        },
        '& .ql-formats button:hover': {
          backgroundColor: '#f5f5f5',
        },
        '& .ql-formats .ql-active': {
          backgroundColor: '#e3e3e3',
        },
      }}
    >
      <Controller
        name={fieldName}
        control={control}
        rules={{
          ...rules,
          validate: value => {
            // Check if the HTML content is actually empty
            const textOnly = value ? value.replace(/<(.|\n)*?>/g, '').trim() : '';
            return textOnly.length > 0 || 'This field is required';
          }
        }}
        render={({ field }) => (
          <ReactQuill
            ref={quillRef}
            value={field.value || ''}
            onChange={(content, delta, source, editor) => {
              // Only update if there's an actual user change
              if (source === 'user') {
                // Clean the content by removing empty paragraphs
                let cleanedContent = cleanHtmlContent(content);
                field.onChange(cleanedContent);
              }
            }}
            onBlur={() => {
              // Additional clean-up on blur to ensure no empty paragraphs remain
              if (field.value) {
                const cleanedContent = cleanHtmlContent(field.value);
                if (cleanedContent !== field.value) {
                  field.onChange(cleanedContent);
                }
              }
            }}
            modules={modules}
            readOnly={disableEditor}
            theme="snow"
            style={{
              height: '220px',
              backgroundColor: disableEditor ? '#f5f5f5' : '#fff',
              cursor: disableEditor ? 'not-allowed' : 'text',
            }}
          />
        )}
      />
    </FormControl>
  );
};

export default TextEditor;

// font family editing is done in this code
// import React, { useState, useRef, useCallback, useEffect } from 'react';
// import { useFormContext, Controller } from 'react-hook-form';
// import ReactQuill from 'react-quill';
// import 'react-quill/dist/quill.snow.css';
// import { FormControl } from '@mui/material';
// import { ImageUploadServices } from '../components/ImgUpload/imageUploadSrevices';
// import Swal from 'sweetalert2';

// // Add custom font to Quill
// const Font = ReactQuill.Quill.import('formats/font');
// // Define the whitelist of fonts
// Font.whitelist = ['arial', 'cabin', 'times-new-roman', 'helvetica'];
// ReactQuill.Quill.register(Font, true);

// // Add custom styles to show proper font names in dropdown
// const fontStyles = `
//   .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="arial"]::before,
//   .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="arial"]::before {
//     content: 'Arial';
//     font-family: 'Arial';
//   }
//   .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="cabin"]::before,
//   .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="cabin"]::before {
//     content: 'Cabin';
//     font-family: 'Cabin';
//   }
//   .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="times-new-roman"]::before,
//   .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="times-new-roman"]::before {
//     content: 'Times New Roman';
//     font-family: 'Times New Roman';
//   }
//   .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="helvetica"]::before,
//   .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="helvetica"]::before {
//     content: 'Helvetica';
//     font-family: 'Helvetica';
//   }
//   .ql-font-arial {
//     font-family: 'Arial';
//   }
//   .ql-font-cabin {
//     font-family: 'Cabin';
//   }
//   .ql-font-times-new-roman {
//     font-family: 'Times New Roman';
//   }
//   .ql-font-helvetica {
//     font-family: 'Helvetica';
//   }
// `;

// const TextEditor = ({ fieldName, rules, imageFolder, hideToolBar, disableEditor }) => {
//   const { control, formState: { errors } } = useFormContext();
//   const quillRef = useRef(null);
//   const [content, setContent] = useState('');

//   // Add styles to document head on component mount
//   useEffect(() => {
//     const styleElement = document.createElement('style');
//     styleElement.textContent = fontStyles;
//     document.head.appendChild(styleElement);
//     return () => styleElement.remove();
//   }, []);

//   // Memoized function to upload an image
//   const uploadImage = useCallback(async (file) => {
//     try {
//       const formData = new FormData();
//       formData.append('file', file);
//       formData.append('folder', imageFolder);

//       Swal.fire({
//         title: 'Uploading Image',
//         text: 'Please wait...',
//         allowOutsideClick: false,
//         allowEscapeKey: false,
//         allowEnterKey: false,
//         showConfirmButton: false,
//         willOpen: () => {
//           Swal.showLoading();
//         },
//       });

//       const response = await ImageUploadServices.uploadImage(formData);
//       Swal.close();

//       return response?.data?.imageBaseUrl + response?.data?.imageDirectory + 'md/' + response?.data?.imageName || null;
//     } catch (error) {
//       console.error('Error uploading file:', error);
//       return null;
//     }
//   }, [imageFolder]);

//   // Image handler for Quill editor
//   const imageHandler = useCallback(() => {
//     const input = document.createElement('input');
//     input.setAttribute('type', 'file');
//     input.setAttribute('accept', 'image/*');
//     input.click();

//     input.onchange = async () => {
//       const file = input.files[0];
//       if (file) {
//         try {
//           const url = await uploadImage(file);
//           if (url && quillRef.current) {
//             const editor = quillRef.current.getEditor();
//             const range = editor.getSelection();
//             editor.insertEmbed(range?.index || 0, 'image', url);
//           }
//         } catch (error) {
//           console.error('Image upload failed:', error);
//         }
//       }
//     };
//   }, [uploadImage]);

//   // Editor modules with image handling
//   const modules = React.useMemo(() => ({
//     toolbar: !hideToolBar ? {
//       container: [
//         [{ font: Font.whitelist }], // Use the whitelist directly
//         [{ header: [1, 2, 3, 4, 5, 6, false] }, { size: ['small', false, 'large', 'huge'] }],
//         ['bold', 'italic', 'underline'],
//         [{ color: [] }, { background: [] }],
//         [{ align: [] }],
//         [{ list: 'ordered' }, { list: 'bullet' }],
//         ['link', 'image', 'video'],
//       ],
//       handlers: {
//         image: imageHandler,
//       },
//     } : false,
//   }), [hideToolBar, imageHandler]);

//   return (
//     <FormControl
//       fullWidth
//       sx={{
//         '& .ql-container': {
//           height: hideToolBar ? '100%' : '90%',
//           fontSize: '0.875rem',
//           backgroundColor:  '#fff',
//         },
//         '& .ql-toolbar.ql-snow': {
//           display: hideToolBar ? 'none' : 'block',
//           borderColor: errors[fieldName] ? '#d32f2f' : '#cccccc',
//           borderWidth: '1px',
//           padding: '4px 8px',
//           '& .ql-formats': {
//             marginRight: '8px',
//             '&:last-child': {
//               marginRight: 0,
//             },
//           },
//           '& button': {
//             height: '24px',
//             width: '24px',
//             padding: '2px',
//           },
//           '& .ql-picker': {
//             height: '24px',
//             '& .ql-picker-label': {
//               padding: '0 4px',
//               fontSize: '0.75rem',
//             },
//             '& .ql-picker-options': {
//               fontSize: '0.75rem',
//             },
//           },
//         },
//         '& .ql-container.ql-snow': {
//           borderColor: errors[fieldName] ? '#d32f2f' : '#cccccc',
//           borderWidth: '1px',
//           fontSize: '0.875rem',
//           backgroundColor: '#fff',
//           cursor: disableEditor ? 'not-allowed' : 'text',
//           '& .ql-editor': {
//             padding: '12px',
//             '& p': {
//               fontSize: '0.875rem',
//             },
//             '& h1': { fontSize: '1.5rem' },
//             '& h2': { fontSize: '1.25rem' },
//             '& h3': { fontSize: '1.125rem' },
//           },
//         },
//         '& .ql-toolbar + .ql-container': {
//           borderTop: hideToolBar ? '1px solid #cccccc' : 'none',
//         },
//         '& .ql-formats button:hover': {
//           backgroundColor: '#f5f5f5',
//         },
//         '& .ql-formats .ql-active': {
//           backgroundColor: '#e3e3e3',
//         },
//       }}
//     >
//       <Controller
//         name={fieldName}
//         control={control}
//         rules={{
//           ...rules,
//           validate: value => {
//             // Check if the HTML content is actually empty
//             const textOnly = value ? value.replace(/<(.|\n)*?>/g, '').trim() : '';
//             return textOnly.length > 0 || 'This field is required';
//           }
//         }}
//         render={({ field }) => (
//           <ReactQuill
          
//             ref={quillRef}
//             value={field.value || ''}
//             onChange={(content, delta, source, editor) => {
//               // Only update if there's an actual user change
//               if (source === 'user') {
//                 field.onChange(content);
//               }
//             }}
//             modules={modules}
//             readOnly={disableEditor} // Disables editing
//             theme="snow"
//             style={{
//               height: '220px',
//               backgroundColor: disableEditor ? '#f5f5f5' : '#fff',
//               cursor: disableEditor ? 'not-allowed' : 'text',
//             }}
//           />
//         )}
//       />
//     </FormControl>
//   );
// };

// export default TextEditor;
